import { Capacitor } from '@capacitor/core';
import { Notification } from './dataService';

// Import notification plugins (will be available after installation)
let LocalNotifications: any;
let PushNotifications: any;

// Dynamic imports to handle missing plugins gracefully
const loadNotificationPlugins = async () => {
  try {
    const localNotificationsModule = await import('@capacitor/local-notifications');
    const pushNotificationsModule = await import('@capacitor/push-notifications');
    LocalNotifications = localNotificationsModule.LocalNotifications;
    PushNotifications = pushNotificationsModule.PushNotifications;
  } catch (error) {
    console.warn('Notification plugins not installed. Run: npm install @capacitor/local-notifications @capacitor/push-notifications');
  }
};

class NotificationService {
  private isInitialized = false;

  async initialize() {
    if (this.isInitialized) return;

    try {
      // Check if running on mobile
      if (Capacitor.isNativePlatform()) {
        // Load notification plugins
        await loadNotificationPlugins();
        
        // Request notification permissions
        await this.requestPermissions();
        
        // Set up notification listeners
        await this.setupNotificationListeners();
        
        this.isInitialized = true;
        console.log('Notification service initialized successfully');
      }
    } catch (error) {
      console.error('Error initializing notification service:', error);
    }
  }

  async requestPermissions() {
    try {
      // Request local notification permissions
      const localPermission = await LocalNotifications.requestPermissions();
      console.log('Local notification permission:', localPermission);

      // Request push notification permissions
      const pushPermission = await PushNotifications.requestPermissions();
      console.log('Push notification permission:', pushPermission);

      return localPermission.display === 'granted' || pushPermission.receive === 'granted';
    } catch (error) {
      console.error('Error requesting notification permissions:', error);
      return false;
    }
  }

  async setupNotificationListeners() {
    if (!LocalNotifications || !PushNotifications) {
      console.warn('Notification plugins not available');
      return;
    }

    // Local notification listeners
    LocalNotifications.addListener('localNotificationReceived', (notification: any) => {
      console.log('Local notification received:', notification);
      this.handleNotificationReceived(notification);
    });

    LocalNotifications.addListener('localNotificationActionPerformed', (notificationAction: any) => {
      console.log('Local notification action performed:', notificationAction);
      this.handleNotificationAction(notificationAction);
    });

    // Push notification listeners
    PushNotifications.addListener('registration', (token: any) => {
      console.log('Push notification registration token:', token);
      this.handleRegistrationToken(token);
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Push notification registration error:', error);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
      console.log('Push notification received:', notification);
      this.handleNotificationReceived(notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (notificationAction: any) => {
      console.log('Push notification action performed:', notificationAction);
      this.handleNotificationAction(notificationAction);
    });
  }

  async showLocalNotification(notification: Notification) {
    try {
      if (!Capacitor.isNativePlatform()) return;

      const localNotification = {
        id: Date.now(),
        title: notification.title,
        body: notification.message,
        sound: 'default',
        schedule: { at: new Date(Date.now() + 1000) }, // Show after 1 second
        actionTypeId: 'OPEN_APP',
        extra: {
          notificationId: notification.id,
          type: notification.type,
          metadata: notification.metadata
        }
      };

      await LocalNotifications.schedule({ notifications: [localNotification] });
      console.log('Local notification scheduled:', localNotification);
    } catch (error) {
      console.error('Error showing local notification:', error);
    }
  }

  async showSupportNotification(adminName: string, messagePreview: string) {
    try {
      if (!Capacitor.isNativePlatform()) return;

      const localNotification = {
        id: Date.now(),
        title: 'Support Reply Received',
        body: `${adminName} has replied to your support request: ${messagePreview}`,
        sound: 'default',
        schedule: { at: new Date(Date.now() + 1000) },
        actionTypeId: 'OPEN_SUPPORT',
        extra: {
          type: 'support',
          adminName,
          messagePreview
        }
      };

      await LocalNotifications.schedule({ notifications: [localNotification] });
      console.log('Support notification scheduled:', localNotification);
    } catch (error) {
      console.error('Error showing support notification:', error);
    }
  }

  async showOrderNotification(orderId: string, status: string) {
    try {
      if (!Capacitor.isNativePlatform()) return;

      const localNotification = {
        id: Date.now(),
        title: 'Order Update',
        body: `Order #${orderId} status: ${status}`,
        sound: 'default',
        schedule: { at: new Date(Date.now() + 1000) },
        actionTypeId: 'OPEN_ORDER',
        extra: {
          type: 'order',
          orderId,
          status
        }
      };

      await LocalNotifications.schedule({ notifications: [localNotification] });
      console.log('Order notification scheduled:', localNotification);
    } catch (error) {
      console.error('Error showing order notification:', error);
    }
  }

  async showPaymentNotification(amount: number, method: string) {
    try {
      if (!Capacitor.isNativePlatform()) return;

      const localNotification = {
        id: Date.now(),
        title: 'Payment Received',
        body: `₹${amount} received via ${method}`,
        sound: 'default',
        schedule: { at: new Date(Date.now() + 1000) },
        actionTypeId: 'OPEN_PAYMENTS',
        extra: {
          type: 'payment',
          amount,
          method
        }
      };

      await LocalNotifications.schedule({ notifications: [localNotification] });
      console.log('Payment notification scheduled:', localNotification);
    } catch (error) {
      console.error('Error showing payment notification:', error);
    }
  }

  private handleNotificationReceived(notification: any) {
    // Handle notification received while app is in foreground
    console.log('Notification received in foreground:', notification);
    
    // You can show an in-app notification here
    // This could trigger the popup notification system
  }

  private handleNotificationAction(notificationAction: any) {
    // Handle notification tap/action
    console.log('Notification action performed:', notificationAction);
    
    const { actionId, notification } = notificationAction;
    const extra = notification.extra;

    // Navigate based on action type
    switch (actionId) {
      case 'OPEN_SUPPORT':
        // Navigate to support chat
        window.location.href = '/settings?openSupport=true';
        break;
      case 'OPEN_ORDER':
        // Navigate to order details
        if (extra?.orderId) {
          window.location.href = `/orders/${extra.orderId}`;
        }
        break;
      case 'OPEN_PAYMENTS':
        // Navigate to payments page
        window.location.href = '/payments';
        break;
      case 'OPEN_APP':
      default:
        // Just open the app
        break;
    }
  }

  private handleRegistrationToken(token: any) {
    // Store the push notification token for server-side notifications
    console.log('Push notification token:', token);
    
    // You can send this token to your server to enable push notifications
    // this.sendTokenToServer(token.value);
  }

  async cancelAllNotifications() {
    try {
      await LocalNotifications.cancel({ notifications: [] });
      console.log('All notifications cancelled');
    } catch (error) {
      console.error('Error cancelling notifications:', error);
    }
  }

  async getNotificationSettings() {
    try {
      const localSettings = await LocalNotifications.checkPermissions();
      const pushSettings = await PushNotifications.checkPermissions();
      
      return {
        local: localSettings,
        push: pushSettings
      };
    } catch (error) {
      console.error('Error getting notification settings:', error);
      return null;
    }
  }
}

export const notificationService = new NotificationService(); 